var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { apiClient } from '@/hoc/api/api-service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// ---------- TRANSFORM ----------
export const transformApiData = (apiObjectives = [], parentNumber = '') => {
    const processNodes = (nodes, pNum, type, parentId) => nodes.map((obj, index) => {
        var _a, _b;
        const currentNumber = pNum ? `${pNum}.${index + 1}` : `${index + 1}`;
        const children = obj.sub_objectives
            ? processNodes(obj.sub_objectives, currentNumber, 'sub-objective', obj.id)
            : [];
        const allQuestions = [
            ...(obj.questions || []).map((q) => (Object.assign(Object.assign({}, q), { isSelected: q.enabled }))),
            ...(obj.coding_questions || []).map((q) => (Object.assign(Object.assign({}, q), { isSelected: q.enabled, question: q === null || q === void 0 ? void 0 : q.description, answer: q === null || q === void 0 ? void 0 : q.expected_solution }))),
        ];
        const allChildrenSelected = children.length > 0 ? children.every((c) => c.isSelected) : true;
        const allQuestionsSelected = allQuestions.length > 0 ? allQuestions.every((q) => q.enabled) : true;
        const isSelected = obj.enabled && allChildrenSelected && allQuestionsSelected;
        const someChildrenSelected = children.some((c) => c.isSelected || c.isIndeterminate);
        const someQuestionsSelected = allQuestions.some((q) => q.enabled);
        const isIndeterminate = !isSelected && (someChildrenSelected || someQuestionsSelected);
        return {
            id: obj.id,
            number: `Objective ${currentNumber}`,
            title: obj.title,
            interview_structure: (_a = obj.interview_structure) !== null && _a !== void 0 ? _a : "",
            percentage: String(obj.score),
            questions: allQuestions,
            children,
            enabled: obj.enabled,
            evaluation_criteria: (_b = obj === null || obj === void 0 ? void 0 : obj.evaluation_criteria) !== null && _b !== void 0 ? _b : [],
            isSelected,
            isIndeterminate,
            type,
            parentId,
        };
    });
    return processNodes(apiObjectives, parentNumber, 'objective');
};
// ---------- OBJECTIVES ----------
export const useGetObjectives = (jdId, options) => useQuery({
    queryKey: ["objectives", jdId],
    queryFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ queryKey }) {
        var _b;
        const [, id] = queryKey;
        // Reuse the latest existing structure by default; only ask the backend to
        // (re)generate when the caller flips the ref (JD changed / Generate with AI).
        // Read at call time so an imperative refetch() always sees the current value.
        const regenerate = ((_b = options === null || options === void 0 ? void 0 : options.regenerateRef) === null || _b === void 0 ? void 0 : _b.current) === true;
        const url = regenerate
            ? `/jd/${id}/objectives/?regenerate=true`
            : `/jd/${id}/objectives/`;
        const res = yield apiClient.get(url);
        return res !== null && res !== void 0 ? res : [];
    }),
    enabled: !!jdId && (options === null || options === void 0 ? void 0 : options.enabled) !== false,
    structuralSharing: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    select: (data) => {
        return transformApiData(data !== null && data !== void 0 ? data : []);
    },
});
export const useUpdateObjective = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ jdId, objectiveId, data, }) => apiClient.put(`/jd/${jdId}/objectives/${objectiveId}/`, data),
        onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['objectives', vars.jdId] }),
    });
};
// ---------- SUBOBJECTIVES ----------
export const useCreateSubobjective = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ jdId, objectiveId, data }) => apiClient.post(`/jd/${jdId}/objectives/${objectiveId}/sub-objectives/`, data),
        onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['objectives', vars.jdId] }),
    });
};
export const useUpdateEvaluationCriteria = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ data, jdId, }) => apiClient.put('/evaluation-criteria/bulk/', data),
        onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['objectives', vars.jdId] }),
    });
};
export const useDeleteQuestion = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ jdId, objectiveId, subobjectiveId, questionId, }) {
            return apiClient.delete(`/jd/${jdId}/objectives/${objectiveId}/sub-objectives/${subobjectiveId}/questions/${questionId}/`);
        }),
        onSuccess: (_, { jdId, objectiveId, subobjectiveId, questionId }) => {
            // ✅ 1. Update the cache manually
            qc.setQueryData(['objectives', jdId], (oldData) => {
                var _a, _b;
                if (!oldData)
                    return oldData;
                // Deep clone to avoid mutating cache directly
                const updated = structuredClone(oldData);
                // Find the correct objective
                const objective = updated.find((obj) => obj.id === objectiveId);
                if (!objective)
                    return updated;
                // Find the correct subobjective
                const subObj = (_a = objective.sub_objectives) === null || _a === void 0 ? void 0 : _a.find((s) => s.id === subobjectiveId);
                if (!subObj)
                    return updated;
                // Remove the deleted question
                subObj.questions = (_b = subObj.questions) === null || _b === void 0 ? void 0 : _b.filter((q) => q.id !== questionId);
                return updated;
            });
            // ✅ 2. Optionally invalidate the query to refresh later
            qc.invalidateQueries({ queryKey: ['objectives', jdId] });
        },
        onError: (error) => {
            console.error('❌ Failed to delete question:', error);
        },
    });
};
export const useUpdateSubobjective = () => {
    // const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ jdId, objectiveId, subobjectiveId, data, }) => apiClient.put(`/jd/${jdId}/objectives/${objectiveId}/sub-objectives/${subobjectiveId}/`, data),
        // onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['objectives', vars.jdId] }),
    });
};
// ---------- QUESTIONS ----------
export const useCreateQuestion = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ jdId, objectiveId, subobjectiveId, data, }) => apiClient.post(`/jd/${jdId}/objectives/${objectiveId}/sub-objectives/${subobjectiveId}/questions/`, data),
        onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['objectives', vars.jdId] }),
    });
};
export const useUpdateQuestion = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ jdId, objectiveId, subobjectiveId, questionId, data, }) => apiClient.put(`/jd/${jdId}/objectives/${objectiveId}/sub-objectives/${subobjectiveId}/questions/${questionId}/`, data),
        onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['objectives', vars.jdId] }),
    });
};
